export const VOICE_END_PAUSE_MS = 3_000;
export const VOICE_MAX_RECORDING_MS = 15_000;
const VOICE_NOISE_CALIBRATION_MS = 500;

export interface VoiceSilenceState {
  readonly startedAtMs: number;
  readonly heardSpeech: boolean;
  readonly silentSinceMs: number | null;
  readonly noiseFloor: number;
  readonly peakLevel: number;
}

export interface VoiceSilenceResult {
  readonly state: VoiceSilenceState;
  readonly shouldStop: boolean;
}

export function createVoiceSilenceState(startedAtMs: number): VoiceSilenceState {
  return {
    startedAtMs,
    heardSpeech: false,
    silentSinceMs: null,
    noiseFloor: 0.006,
    peakLevel: 0,
  };
}

export function advanceVoiceSilence(
  state: VoiceSilenceState,
  rms: number,
  nowMs: number,
): VoiceSilenceResult {
  if (nowMs - state.startedAtMs >= VOICE_MAX_RECORDING_MS) {
    return { state, shouldStop: true };
  }
  const level = Number.isFinite(rms) ? Math.max(0, rms) : 0;
  if (!state.heardSpeech) {
    const elapsedMs = nowMs - state.startedAtMs;
    const calibrating = elapsedMs < VOICE_NOISE_CALIBRATION_MS;
    const noiseFloor = calibrating
      ? state.noiseFloor * 0.72 + level * 0.28
      : state.noiseFloor;
    const speechThreshold = Math.max(0.016, Math.min(0.055, noiseFloor * 2));
    const heardSpeech = level >= 0.06 || (!calibrating && level >= speechThreshold);
    return {
      state: {
        ...state,
        heardSpeech,
        noiseFloor,
        peakLevel: heardSpeech ? level : state.peakLevel,
        silentSinceMs: null,
      },
      shouldStop: false,
    };
  }
  const peakLevel = Math.max(state.peakLevel, level);
  const silenceThreshold = Math.max(
    0.012,
    Math.min(0.05, Math.max(state.noiseFloor * 1.5, peakLevel * 0.38)),
  );
  if (level > silenceThreshold) {
    return { state: { ...state, peakLevel, silentSinceMs: null }, shouldStop: false };
  }
  const silentSinceMs = state.silentSinceMs ?? nowMs;
  return {
    state: { ...state, peakLevel, silentSinceMs },
    shouldStop: nowMs - silentSinceMs >= VOICE_END_PAUSE_MS,
  };
}
